import React, { CSSProperties, memo, useCallback, useMemo, useState } from 'react'
import { useStillAlive } from 'hooks'

/** @ts-ignore */
import s from './index.module.sass'

export interface ButtonProps {
  icon?: 'stop' | 'delete' | 'edit' | 'like' | 'run'
  modifier?: 'postive' | 'negtive' | 'red' | 'blue' | 'liked'
  style?: CSSProperties
  className?: string
  text: string
  size?: 'medium' | 'small' | 'mini' | 'c48'
  children?: never
  onClick?: (e: Parameters<React.MouseEventHandler>) => void | Promise<unknown>
  noShowDotDotDot?: boolean
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default memo(function Button<T extends ButtonProps>({ noShowDotDotDot, icon, modifier, text, className: receivedClassName, children, style, size, ...props }: T) {
  const [preventClick, setPreventClick] = useState(false)
  const isAlive = useStillAlive()
  const className = useMemo(() => {
    const defaultName = [s['btn']]
    if (modifier) {
      defaultName.push(s[modifier])
    }
    if (icon != null) {
      defaultName.push(s['has-icon'])
    }
    if (size) {
      defaultName.push(s[size])
    }
    if (receivedClassName) {
      defaultName.push(receivedClassName)
    }
    return defaultName.join(' ')
  }, [icon, modifier, receivedClassName])

  const child = useMemo(() => {
    const ret: React.ReactNode[] = [text + (!noShowDotDotDot && preventClick ? '...' : '')]

    if (icon != null) {
      ret.unshift(<div className={`${s['icon']} ${s[icon]}`} key="icon"></div>)
    }
    return ret
  }, [icon, preventClick, text])

  const onClickProxy = useCallback((e) => {
    if (preventClick || !props.onClick) {
      return
    }
    const mayPromise = props.onClick(e)
    if (mayPromise != null && typeof mayPromise.then === 'function') {
      setPreventClick(true)
      mayPromise.then(() => {
        if (isAlive()) {
          setPreventClick(false)
        }
      }, (err) => {
        if (isAlive()) {
          setPreventClick(false)
        }
        throw err
      })
    }
  }, [preventClick, props.onClick, setPreventClick])

  return <div className={className} style={style} onClick={onClickProxy}>
    {child}
  </div>
})